const cities = [
  { name: "Santa Rosa", zips: "95401, 95403, 95404, 95405, 95407, 95409" },
  { name: "Petaluma", zips: "94952, 94953, 94954" },
  { name: "Rohnert Park", zips: "94928" },
  { name: "Windsor", zips: "95492" },
  { name: "Healdsburg", zips: "95448" },
  { name: "Sebastopol", zips: "95472, 95473" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your Oakmont home, vineyard office, or SRJC dorm in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function SantaRosaLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Santa Rosa &amp; Sonoma County
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Santa Rosa is the largest city in Sonoma County and the heart of one of the world's most recognized wine regions. The Sonoma wine industry generates significant international travel demand — vineyard owners, winemakers, and hospitality executives regularly travel to Bordeaux, Burgundy, Tuscany, and the Rioja region for buyer relationships, harvest consultations, and international wine competitions. These trips are often confirmed weeks out, not months in advance, which creates chronic urgency around passport renewals and replacements.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond the wine industry, Santa Rosa is a significant healthcare and education hub. Kaiser Permanente, Sutter Health, and Santa Rosa Junior College all operate major facilities here. Healthcare professionals attend international medical conferences on short notice. SRJC students and faculty pursue international study and research programs with deadlines tied to semester calendars, not passport processing queues.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The Oakmont retirement community — one of California's largest active adult communities — generates steady demand for senior passport renewals from residents planning European river cruises, Mediterranean tours, and Latin American adventures. Many Oakmont residents discover their passports expired during the pandemic years when international travel was suspended, and they need expedited processing before their next bucket-list trip.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center serves Santa Rosa and all of Sonoma County with the same expedited passport processing that we provide nationwide. The San Francisco Passport Agency is about 55 miles south — but public appointments are scarce and the commute is a full day. We provide the simple, reliable alternative: start online from your Santa Rosa home or business, ship overnight, and receive your passport within your selected timeframe.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Santa Rosa, CA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Santa Rosa & all of Sonoma County</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — ship overnight from Santa Rosa, Petaluma, or Healdsburg
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">No office visit required for most services</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start online, follow your personalized checklist, and ship your documents overnight. We review everything before submission and keep you updated throughout the process.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Sonoma County — start online, ship overnight, and we handle the rest. No need to drive to the SF Passport Agency.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "San Francisco", href: "/san-francisco" },
                    { name: "Oakland", href: "/oakland" },
                    { name: "Sacramento", href: "/sacramento" },
                    { name: "Vallejo", href: "/vallejo" },
                    { name: "Napa", href: "/states/california" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Santa Rosa Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}